/**
 * The legal text of the Mondoway Terms & Conditions / Privacy Policy.
 *
 * This file is CONTENT, not presentation — every string here is binding legal
 * wording carried over verbatim. Change it only deliberately, and keep
 * `LAST_UPDATED` in step with any edit.
 *
 * Block shapes understood by the renderer:
 *   { p }                     paragraph
 *   { ul }                    bulleted list
 *   { h3 }                    sub-heading
 *   { callout }               highlighted note: { tone, icon, title, text }
 *   { split }                 two-column permitted / prohibited comparison
 */

export const LAST_UPDATED = "09/12/2025";

export const LEGAL_SECTIONS = [
  {
    id: "introduction",
    title: "Introduction",
    blocks: [
      {
        p: "Welcome to MONDOWAY, a multifunctional digital platform that enables you to easily and securely buy, sell, and rent products and services. By using this application, you agree to comply with these Terms & Conditions, which govern the legal relationship between you as a user and the application management."
      }
    ]
  },
  {
    id: "account-creation",
    title: "Account Creation",
    blocks: [
      {
        ul: [
          "Registration is completely free of charge.",
          "Accurate and up-to-date information must be provided when creating an account.",
          "The management reserves the right to suspend or delete any account found to be in violation of these terms."
        ]
      }
    ]
  },
  {
    id: "bazaar",
    title: 'Use of the "Bazaar" Section',
    blocks: [
      {
        p: "The Bazaar section allows users to post advertisements for selling or renting products and services, including:"
      },
      {
        ul: [
          "The ability to post free ads.",
          "Editing ads at any time.",
          "Deleting ads at any time.",
          "Deleting the entire account easily from within the application without needing to contact management."
        ]
      }
    ]
  },
  {
    id: "content-rules",
    title: "Permitted and Prohibited Content",
    blocks: [
      {
        split: {
          permittedTitle: "Permitted to post:",
          permitted: [
            "Used personal items.",
            "Real estate, vehicles, electronics, furniture, personal services, and others.",
            "Legal offers that do not violate laws or social norms."
          ],
          prohibitedTitle: "Prohibited to post:",
          prohibited: [
            "Weapons and ammunition of all kinds.",
            "Narcotics or any substances prohibited by law.",
            "Political, sectarian, or racist content.",
            "Immoral or pornographic images and videos.",
            "Counterfeit or unlicensed goods.",
            "Fraudulent or misleading offers."
          ]
        }
      },
      {
        callout: {
          tone: "warning",
          icon: "alert",
          text: "Any violating content will be removed without prior notice, and accounts may be permanently banned."
        }
      }
    ]
  },
  {
    id: "payments",
    title: "Payments and Fees",
    blocks: [
      {
        ul: [
          "Use of the application and basic services is entirely free.",
          "Optional paid services may be available, such as promoting ads or pinning them to the top.",
          "All payments are exclusively processed via secure and encrypted electronic payment gateways using credit cards.",
          "Users bear full responsibility for the accuracy of payment details.",
          "No refunds will be issued once a service has been successfully executed."
        ]
      }
    ]
  },
  {
    id: "privacy",
    title: "Privacy",
    blocks: [
      {
        p: "This Privacy Policy explains how our delivery application (“App”) collects, uses, and protects your information. By using the App, you agree to the terms of this Privacy Policy."
      },
      { h3: "1. Information We Collect" },
      {
        p: "Location Data — the App collects and uses location data for delivery-related features. We collect:"
      },
      {
        ul: [
          "Precise location (GPS)",
          "Approximate location",
          "Background location (when the driver is online or actively delivering an order)"
        ]
      },
      { h3: "2. Why We Collect Location Data" },
      { p: "Location data is used to:" },
      {
        ul: [
          "Assign delivery orders to nearby drivers",
          "Track the driver’s location during active deliveries",
          "Provide accurate navigation and routing",
          "Display real-time driver location to customers and restaurants",
          "Improve delivery accuracy and service efficiency"
        ]
      },
      {
        callout: {
          tone: "info",
          icon: "pin",
          title: "3. Background Location Access",
          text: "When a driver goes online or is handling a delivery, the App may access location data even if the App is closed or running in the background. This is required to ensure order tracking, navigation accuracy, and timely updates to customers and restaurants. The App does not access background location when the driver is offline."
        }
      },
      { h3: "4. Data Sharing" },
      {
        p: "We do not sell location data to any third parties. We only share location data with:"
      },
      {
        ul: [
          "The customer (for tracking the order)",
          "The restaurant (for preparation and coordination)",
          "Our backend servers (to manage delivery operations)"
        ]
      },
      {
        callout: {
          tone: "neutral",
          icon: "sliders",
          title: "5. User Control",
          text: "Users can disable location permissions at any time from device settings. However, disabling location access will prevent the driver from receiving or completing delivery orders."
        }
      },
      { h3: "6. Data Security" },
      {
        p: "We take reasonable measures to protect your information from unauthorized access, alteration, or disclosure."
      },
      { h3: "Additional Privacy Commitments" },
      {
        ul: [
          "The management respects user privacy and does not share data with any external parties without prior consent. Our privacy practices include the collection and use of location data (precise location (GPS), approximate location, and background location) for delivery features, as described in the Location Data Policy and Privacy Policy sections below.",
          "Users can delete their accounts along with all their data and ads permanently through the account settings inside the app."
        ]
      }
    ]
  },
  {
    id: "location-data",
    title: "Location Data Policy",
    blocks: [
      {
        p: "Our application collects and uses location data for delivery-related features. We collect:"
      },
      {
        ul: [
          "Precise location (GPS)",
          "Approximate location",
          "Background location (when the driver is online or delivering an order)"
        ]
      },
      { h3: "Purpose of Using Location" },
      { p: "Location data is used to:" },
      {
        ul: [
          "Assign delivery orders to nearby drivers",
          "Track the driver's route during active deliveries",
          "Show real-time driver location to the restaurant and customer",
          "Improve navigation and delivery accuracy"
        ]
      },
      {
        callout: {
          tone: "info",
          icon: "pin",
          title: "Background Location",
          text: "When a driver goes online or is in the process of completing a delivery, the app may access location data even when the app is closed or running in the background. This is required to ensure proper order tracking, accurate routing, and timely updates."
        }
      },
      { h3: "Data Sharing" },
      {
        p: "We do not sell or share location data with third parties. Location information is only shared with:"
      },
      {
        ul: [
          "The customer (to track the order)",
          "The restaurant (for order preparation)",
          "Our backend servers for delivery coordination"
        ]
      },
      {
        callout: {
          tone: "neutral",
          icon: "sliders",
          title: "User Control",
          text: "Drivers can disable location access at any time from the device settings, but doing so will prevent them from receiving new orders or completing active deliveries."
        }
      }
    ]
  },
  {
    id: "intellectual-property",
    title: "Intellectual Property Rights",
    blocks: [
      {
        ul: [
          "All intellectual property rights related to the application's design, content, and services are reserved by MONDOWAY management.",
          "Users bear full responsibility for the content they post and commit not to infringe upon third-party intellectual property rights or trademarks."
        ]
      }
    ]
  },
  {
    id: "product-disclaimer",
    title: "Disclaimer Regarding Products",
    blocks: [
      {
        ul: [
          "MONDOWAY assumes no responsibility for the quality, condition, or any defects of the products listed.",
          "Both sellers and buyers are fully responsible for verifying the product condition and the accuracy of specifications before completing any transaction."
        ]
      }
    ]
  },
  {
    id: "amendments",
    title: "Amendments to the Terms",
    blocks: [
      {
        ul: [
          "The management reserves the right to modify these Terms and Conditions at any time.",
          "Continued use of the application after any changes constitutes implicit acceptance of the updated terms."
        ]
      }
    ]
  },
  {
    id: "business-users",
    title: "Terms and Conditions for Business Users (Merchants)",
    blocks: [
      { h3: "1. Introduction" },
      {
        p: "These Terms and Conditions govern the use of MONDOWAY's Business Account services (\"the Platform\") by registered merchants and business users. By creating or using a Business Account, you agree to be bound by these Terms and Conditions, as well as MONDOWAY's general policies, privacy policy, and applicable laws."
      },
      { h3: "2. Business Account Registration" },
      {
        ul: [
          "The merchant must provide complete and accurate information when registering a Business Account, including: Company name and contact details, valid commercial registration certificate, tax number (if applicable), and any additional licenses required by law.",
          "The merchant must upload a valid commercial registration document to verify business identity and compliance with legal requirements.",
          "MONDOWAY reserves the right to verify the authenticity of any provided information or documents and to suspend or terminate any account providing false or misleading information."
        ]
      },
      { h3: "3. Profile Management" },
      {
        ul: [
          "The merchant has full control over their Business Profile, including the ability to create, edit, and update profile information; upload business logos, descriptions, and promotional materials; and delete the entire profile at any time via account settings.",
          "MONDOWAY may retain certain transactional data for legal or auditing purposes even after account deletion, in accordance with applicable laws."
        ]
      },
      { h3: "4. Product and Advertisement Posting" },
      {
        ul: [
          "Merchants may freely upload, edit, or remove their products or advertisements on the platform's Home Page, Showroom, or any other marketplace section.",
          "Each merchant is responsible for setting their own prices, offers, and product availability.",
          "All published advertisements must comply with MONDOWAY's content standards and applicable legal requirements."
        ]
      },
      { h3: "5. Content and Legal Compliance" },
      {
        ul: [
          "It is strictly prohibited to post: immoral or inappropriate images; weapons or ammunition; illegal substances; counterfeit goods; or political/religiously offensive content.",
          "Any food, supplement, or drug-related product must have proper licensing from the Jordan Food and Drug Administration (JFDA) or equivalent.",
          "Real estate or accommodation ads must comply with relevant advertising laws.",
          "MONDOWAY reserves the right to remove any violating advertisement without prior notice."
        ]
      },
      { h3: "6. Merchant Responsibilities" },
      {
        ul: [
          "The merchant shall ensure all content is lawful, accurate, and compliant with intellectual property and consumer protection laws.",
          "The merchant bears full legal responsibility for all content, products, and transactions.",
          "The merchant must respond promptly to customer inquiries and complaints per MONDOWAY's dispute policy."
        ]
      },
      { h3: "7. Platform Rights and Limitations" },
      {
        ul: [
          "MONDOWAY may monitor, review, or remove any content at its sole discretion.",
          "MONDOWAY is not responsible for the accuracy or legality of merchant-posted content.",
          "Repeated violations may result in account suspension or permanent deletion without compensation."
        ]
      },
      { h3: "8. Payment, Fees, and Commissions" },
      {
        ul: [
          "Merchants agree to pay applicable fees or commissions as per MONDOWAY's pricing policy.",
          "Commissions may be automatically deducted from transactions.",
          "Failure to maintain sufficient balance in the linked wallet (e.g., Zain Cash) may suspend new orders or ads."
        ]
      },
      { h3: "9. Intellectual Property" },
      {
        p: "All trademarks, logos, software, and design elements of MONDOWAY are the property of MONDOWAY and may not be copied, modified, or reused without prior written approval."
      },
      { h3: "10. Termination" },
      {
        ul: [
          "The merchant may terminate their account at any time via settings.",
          "MONDOWAY may suspend or terminate any violating Business Account.",
          "Upon termination, all active listings will be removed from the platform."
        ]
      },
      { h3: "11. Liability Disclaimer" },
      {
        p: "MONDOWAY is a digital intermediary platform and shall not be held liable for: misrepresentation by merchants, losses from third-party transactions, or violations committed by the merchant."
      },
      { h3: "12. Amendments" },
      {
        p: "MONDOWAY reserves the right to modify these Terms at any time. Continued use constitutes acceptance of revised terms."
      },
      { h3: "13. Governing Law and Jurisdiction" },
      {
        p: "These Terms are governed by the laws of the Hashemite Kingdom of Jordan. Any disputes shall be settled by the competent courts in Amman."
      },
      { h3: "14. Acknowledgment" },
      {
        p: "By registering as a Business User on MONDOWAY, you acknowledge that you have read, understood, and agreed to all the above terms, and you commit to complying with all legal and ethical standards when using the platform."
      }
    ]
  },
  {
    id: "drivers",
    title: "MONDOWAY Driver Terms and Conditions",
    blocks: [
      { h3: "1. Introduction" },
      {
        p: "These Terms and Conditions govern the relationship between MONDOWAY (hereinafter referred to as \"the Company\") and drivers registered on the MONDOWAY application (hereinafter referred to as \"the Driver\"). By registering, activating, or using the MONDOWAY Driver App, the Driver acknowledges full acceptance of these Terms and agrees to comply with all policies, procedures, and operational standards set forth by the Company."
      },
      { h3: "2. Driver Eligibility" },
      {
        ul: [
          "The Driver must be at least 18 years old and possess a valid driver's license recognized in the operating jurisdiction.",
          "The Driver must own or legally operate a vehicle that meets MONDOWAY's technical and safety requirements.",
          "The Driver must maintain a clean criminal record and provide ID, vehicle registration, and insurance as requested.",
          "The Company reserves the right to reject or suspend any non-compliant Driver."
        ]
      },
      { h3: "3. Use of the Application" },
      {
        ul: [
          "The Driver is responsible for login credential confidentiality and liable for all account activity.",
          "The app shall only be used for legitimate delivery/transportation services.",
          "Unauthorized use (e.g., account transfer, third-party software, request manipulation) is prohibited.",
          "Violation may result in suspension or termination."
        ]
      },
      { h3: "4. Service Standards" },
      {
        ul: [
          "Services must be professional, courteous, timely, and safe.",
          "Deliveries must follow MONDOWAY's procedures and customer instructions.",
          "Drivers must represent MONDOWAY positively and avoid reputation-harming behavior.",
          "Repeated complaints, late deliveries, or misconduct may lead to account suspension."
        ]
      },
      { h3: "5. Financial Policy (Zain Cash Wallet)" },
      {
        ul: [
          "Drivers must maintain a positive Zain Cash Wallet balance to cover commissions.",
          "Commissions are auto-deducted after each order.",
          "If the balance is zero or negative, new delivery requests stop until recharged.",
          "A minimum balance may be required to keep the account active.",
          "Earnings are transferred daily at midnight after deductions.",
          "Financial claims are cleared after daily transfer; system reports are final.",
          "Repeated failure to recharge may cause suspension.",
          "The wallet is operational only—not a savings account.",
          "Upon termination, a final settlement may be requested after deductions."
        ]
      },
      { h3: "6. Earnings and Payouts" },
      {
        ul: [
          "All customer payments are processed via MONDOWAY's system and paid per schedule.",
          "Payment disputes must be reported within 48 hours.",
          "MONDOWAY may withhold payments in cases of fraud, misuse, or unresolved complaints."
        ]
      },
      { h3: "7. Suspension and Termination" },
      {
        ul: [
          "The Company may suspend or terminate accounts for violations or misconduct.",
          "Upon termination, pending payments are settled after deductions.",
          "Drivers may request voluntary termination via support."
        ]
      },
      { h3: "8. Data Privacy" },
      {
        ul: [
          "Driver data is processed per data protection laws.",
          "Data is used for operational/verification purposes and not shared without consent, except as required by law."
        ]
      },
      { h3: "9. Liability and Insurance" },
      {
        ul: [
          "The Driver is fully responsible for damages or incidents during service.",
          "The Company is not liable for accidents, traffic violations, or personal damages.",
          "Drivers must maintain valid vehicle insurance and comply with commercial operation laws."
        ]
      },
      { h3: "10. Modifications" },
      {
        p: "MONDOWAY reserves the right to modify these Terms at any time. Changes will be communicated via app or email. Continued use constitutes acceptance."
      },
      { h3: "11. Governing Law and Jurisdiction" },
      {
        p: "These Terms are governed by the laws of the Hashemite Kingdom of Jordan. Any disputes shall be subject to the exclusive jurisdiction of the competent courts in Amman, Jordan."
      }
    ]
  }
];
